import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { getUrlParams } from '../helpers';
import SessionStorage from '../SessionStorage';

const generateSessionId = () => {
  const characters = Array.from(Array(20), () => Math.floor(Math.random() * 36).toString(36));

  // Two positions are always the same, to detect bots that generate their own session IDs.
  // eslint-disable-next-line prefer-destructuring
  characters[12] = characters[4];

  return characters.join('');
};

const getSessionIdFromUrl = () => {
  const { sessionId } = getUrlParams();

  if (typeof sessionId === 'string') {
    return sessionId;
  }

  return null;
};

const initializedSessionId: string = getSessionIdFromUrl()
  || SessionStorage.getItem('session_id') || generateSessionId();

export const getSessionId = () => {
  // Store the session ID again in case the user's session storage was cleared.
  storeSessionId(initializedSessionId);

  return initializedSessionId;
};

const storeSessionId = (sessionId: string) => {
  SessionStorage.setItem('session_id', sessionId);
};

interface TrackingProviderProps {
  children: ReactNode;
  defaultPayload?: any;
}

interface TrackingContextValue {
  track: (name: string, payload?: any) => void;
}

const TrackingContext = React.createContext({} as TrackingContextValue);

export const TrackingProvider = ({ defaultPayload, children }: TrackingProviderProps) => {
  const timerRef = useRef<number>();
  const queueRef = useRef([]);

  const defaultPayloadRef = useRef(defaultPayload);
  useEffect(() => {
    defaultPayloadRef.current = defaultPayload;
  });

  const emptyQueue = useCallback(() => {
    if (queueRef.current.length > 0) {
      trackEvents({
        events: queueRef.current.map((trackingEvent) => ({
          ...trackingEvent,
          payload: JSON.stringify({
            ...defaultPayloadRef.current,
            ...trackingEvent?.payload,
          }),
        })),
      });

      queueRef.current = [];
    }
  }, []);

  const track = useCallback((name: string, payload?: any) => {
    window.clearTimeout(timerRef.current);

    queueRef.current.push({
      name,
      payload,
      timestamp: Math.floor((new Date()).getTime() / 1000),
    });

    timerRef.current = window.setTimeout(emptyQueue, 1000);
  }, [emptyQueue]);

  const value = useMemo(() => ({ track }), [track]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

/**
 * Sends the tracking events to the server using a basic XMLHttpRequest to cause the least
 * amount of overhead, so that this method can be called in clean-up functions such as
 * window.onbeforeunload().
 */
const trackEvents = (data: any) => {
  const sessionId = getSessionId();

  const request = new XMLHttpRequest();
  request.open('POST', '/api/track');
  request.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
  if (sessionId) {
    request.setRequestHeader('Atleta-Session-ID', sessionId);
  }
  request.send(JSON.stringify(data));
};

export const useTracking = () => useContext(TrackingContext);

export const usePageViewTracking = (tag: string | null, payload: object = {}) => {
  const { track } = useTracking();

  const tagRef = useRef<string | null>(null);
  const payloadRef = useRef(payload);

  useEffect(() => {
    payloadRef.current = payload;
  });

  // Send a tracking event each time the tag changes (and is not null).
  useEffect(() => {
    const newTag = tag || null;

    if (tagRef.current === newTag) {
      return;
    }

    if (newTag) {
      track?.('PageView', { tag: newTag, ...payloadRef.current });
    }

    tagRef.current = newTag;
  }, [track, tag]);
};

export default undefined;
